import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Listbox } from '@/components/ui/listbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { churchToday, formatDate } from '@/utils/format';

import { PastoralSubnav } from './pastoral-subnav';
import { useMemberPicker } from './use-member-picker';

const VISIT_KINDS = ['home', 'hospital', 'workplace', 'other'] as const;
type VisitKind = (typeof VISIT_KINDS)[number];

interface VisitRow {
  id: string;
  memberId: string;
  visitedByMemberId: string;
  visitDate: string;
  kind: VisitKind;
  summary: string | null;
  followUpNeeded: boolean;
}

export const VisitsPage = () => {
  const { t } = useTranslation('pastoral');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canRecord = hasPermission(membership, PERMISSIONS.PASTORAL_RECORD_VISIT);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [open, setOpen] = useState(false);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [visitedByMemberId, setVisitedByMemberId] = useState<string | null>(null);
  const [visitDate, setVisitDate] = useState<string | null>(churchToday());
  const [kind, setKind] = useState<VisitKind>('home');
  const [summary, setSummary] = useState('');

  const memberPicker = useMemberPicker(open, 'pastoral-visit-member');
  const visitorPicker = useMemberPicker(open, 'pastoral-visit-by');

  const list = useQuery({
    queryKey: ['pastoral', 'visits', page, pageSize],
    queryFn: () =>
      api.get<{ data: VisitRow[]; page: OffsetPage }>('/pastoral/visits', {
        query: { page, pageSize },
      }),
    placeholderData: keepPreviousData,
  });

  const reset = () => {
    setMemberId(null);
    setVisitedByMemberId(null);
    setVisitDate(churchToday());
    setKind('home');
    setSummary('');
    memberPicker.setSelected(null);
    visitorPicker.setSelected(null);
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<VisitRow>('/pastoral/visits', {
        memberId,
        visitedByMemberId,
        visitDate,
        kind,
        summary: summary.trim() || null,
      }),
    onSuccess: async () => {
      toast.success(t('visits.saved'));
      setOpen(false);
      reset();
      await queryClient.invalidateQueries({ queryKey: ['pastoral', 'visits'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const columns: DataTableColumn<VisitRow>[] = [
    {
      id: 'visitDate',
      header: t('visits.columns.visitDate'),
      cell: (row) => formatDate(row.visitDate),
    },
    {
      id: 'kind',
      header: t('visits.columns.kind'),
      cell: (row) => t(`visits.kind.${row.kind}`),
    },
    {
      id: 'summary',
      header: t('visits.columns.summary'),
      cell: (row) => row.summary || '—',
    },
    {
      id: 'followUp',
      header: t('visits.columns.followUp'),
      cell: (row) =>
        row.followUpNeeded ? (
          <Badge variant="warning">{t('visits.followUp.needed')}</Badge>
        ) : (
          t('visits.followUp.none')
        ),
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;
  const canSubmit = Boolean(memberId && visitedByMemberId && visitDate);

  return (
    <div>
      <PageHeader
        title={t('visits.title')}
        description={t('visits.description')}
        headerActions={
          canRecord ? <Button onClick={() => setOpen(true)}>{t('visits.create')}</Button> : null
        }
      />

      <PastoralSubnav />

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={
          <div className="space-y-3 py-4 text-center">
            <p className="text-body font-medium">{t('visits.empty.title')}</p>
            <p className="text-body-sm text-fg-secondary">{t('visits.empty.description')}</p>
            {canRecord ? (
              <div className="flex justify-end sm:justify-center">
                <Button onClick={() => setOpen(true)}>{t('visits.empty.action')}</Button>
              </div>
            ) : null}
          </div>
        }
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <div className="mt-4">
          <Pagination
            page={pageMeta.page}
            pageSize={pageMeta.pageSize}
            totalItems={pageMeta.totalItems}
            totalPages={pageMeta.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            labels={{
              showing: ({ from, to, total }) =>
                tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </div>
      ) : null}

      <FormDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) reset();
        }}
        title={t('visits.create')}
        submitLabel={t('visits.save')}
        cancelLabel={tCommon('action.cancel')}
        submitting={create.isPending}
        submitDisabled={!canSubmit}
        size="lg"
        onSubmit={() => create.mutate()}
      >
        <FormField label={t('visits.fields.member')} required>
          {(field) => (
            <EntityCombobox
              id={field.id}
              value={memberId}
              onChange={(id) => {
                setMemberId(id);
                memberPicker.select(id);
              }}
              options={memberPicker.options}
              selected={memberPicker.selected}
              onQueryChange={memberPicker.setQuery}
              loading={memberPicker.loading}
              placeholder={t('memberPicker.placeholder')}
              emptyMessage={t('memberPicker.empty')}
              clearLabel={tCommon('action.clearSelection')}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('visits.fields.visitedBy')} required>
          {(field) => (
            <EntityCombobox
              id={field.id}
              value={visitedByMemberId}
              onChange={(id) => {
                setVisitedByMemberId(id);
                visitorPicker.select(id);
              }}
              options={visitorPicker.options}
              selected={visitorPicker.selected}
              onQueryChange={visitorPicker.setQuery}
              loading={visitorPicker.loading}
              placeholder={t('memberPicker.placeholder')}
              emptyMessage={t('memberPicker.empty')}
              clearLabel={tCommon('action.clearSelection')}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('visits.fields.visitDate')} required>
          {(field) => (
            <DatePicker
              id={field.id}
              value={visitDate}
              onChange={setVisitDate}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('visits.fields.kind')} required>
          {(field) => (
            <Listbox
              id={field.id}
              value={kind}
              onChange={(value) => setKind((value as VisitKind) ?? 'home')}
              options={VISIT_KINDS.map((value) => ({
                value,
                label: t(`visits.kind.${value}`),
              }))}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('visits.fields.summary')}>
          {(field) => (
            <Textarea
              id={field.id}
              value={summary}
              onChange={(event) => setSummary(event.target.value)}
              rows={3}
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>
      </FormDialog>
    </div>
  );
};
